import { FC, Dispatch, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  BreakableName,
  BytesToReadableSize,
  FileFormatLabel,
  FileFullName,
  FormatDateFE,
} from "../../../helper/general/methods";
import { UserDecisionInterface } from "../../../middleware/interfaces/user/user-decision-interface";
import { FileInterface } from "../../../middleware/interfaces/file/file-interface";
import { PdfRetrieveInterface } from "../../../middleware/interfaces/file/pdf-retrieve-interface";
import { UserInterface } from "../../../middleware/interfaces/user/user-interface";
import MenuButton from "../user/menu-button";
import { FileIconSource } from "./file-icon";

interface fileInfoProps {
  selectedFile: FileInterface;
  user: UserInterface;
  setSelectedFile: Dispatch<any>;
  setFileVersionDialogReveal: Dispatch<React.SetStateAction<boolean>>;
  dispatch: Dispatch<any>;
}

const FileInfo: FC<fileInfoProps> = ({
  selectedFile,
  user,
  setSelectedFile,
  setFileVersionDialogReveal,
  dispatch,
}) => {
  const [deleteFileDialogReveal, setDeleteFileDialogReveal] = useState(false);
  const [viewerOptionsDialogReveal, setViewerOptionsDialogReveal] =
    useState(false);
  const [downloadFile, setDownloadFile] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (deleteFileDialogReveal) {
      const decision: UserDecisionInterface = {
        title: `Delete ${selectedFile.fileName}?`,
        message:
          "The file and every version of it will be removed. This cannot be undone.",
        confirmLabel: "Delete",
        decisionOptions: ["DELETE_FILE", "NO_ACTION"],
        result: "",
        data: {
          email: user.email,
          fileID: selectedFile.fileID,
          fileParentID: user.openFolder?.folderID! ?? null,
          fileName: selectedFile.fileName,
          fileType: selectedFile.fileType,
          projectID: user.openProject?.projectID!,
          projectUserRole: user.openProject?.projectUserRole!,
          type: "FileDeleteInterface",
        },
        type: "UserDecisionInterface",
      };
      dispatch({ type: "DECISION", decision: decision });
    }
    setDeleteFileDialogReveal(false);
  }, [deleteFileDialogReveal]);

  useEffect(() => {
    if (viewerOptionsDialogReveal) {
      const decision: UserDecisionInterface = {
        title: "Open in 3D viewer",
        message: "Choose the environment to open the model in.",
        // 4D and MAP join the list once those viewers are built
        decisionOptions: ["STANDARD"],
        imageOptions: true,
        result: "",
        type: "UserDecisionInterface",
      };
      dispatch({ type: "DECISION", decision: decision });
    }
    setViewerOptionsDialogReveal(false);
  }, [viewerOptionsDialogReveal]);

  useEffect(() => {
    if (downloadFile === true) {
      const decision: UserDecisionInterface = {
        title: `Download ${selectedFile.fileName}?`,
        message: "The active version is prepared and saved to your computer.",
        confirmLabel: "Download",
        decisionOptions: ["DOWNLOAD_FILE", "NO_ACTION"],
        result: "",
        data: {
          email: user.email,
          projectUserRole: user.openProject!.projectUserRole,
          projectID: user.openProject?.projectID!,
          fileID: selectedFile.fileID,
          fileName: selectedFile.fileName,
          fileType: selectedFile.fileType,
          fileActiveVersion: selectedFile.fileActiveVersion,
          fileParentID: user.openFolder?.folderID! ?? null,
          type: "FileDownloadInterface",
        },
        type: "UserDecisionInterface",
      };
      dispatch({ type: "DECISION", decision: decision });
    }
    setDownloadFile(false);
  }, [downloadFile]);

  return (
    <>
      {(() => {
        if (selectedFile) {
          return (
            <div className="itemDetails">
              <div className="itemDetailsHeader">
                <img
                  className="icon-medium"
                  src={FileIconSource(selectedFile.fileType)}
                  draggable="false"
                  alt={selectedFile.fileName}
                />
                <div className="itemDetailsIdentity">
                  <span className="itemDetailsName">
                    {BreakableName(
                      FileFullName(selectedFile.fileName, selectedFile.fileType)
                    )}
                  </span>
                  <span className="itemDetailsSubtitle">
                    {BytesToReadableSize(
                      selectedFile.fileVersions[selectedFile.fileActiveVersion]
                        .fileSize
                    )}
                  </span>
                </div>
                <span
                  className="itemDetailsClose"
                  onClick={() => {
                    setSelectedFile(null);
                  }}
                >
                  ×
                </span>
              </div>
              <div className="itemDetailsMetadata">
                <span className="itemDetailsLabel">FORMAT</span>
                <span className="itemDetailsValue">
                  {FileFormatLabel(selectedFile.fileType)}
                </span>
                <span className="itemDetailsLabel">VERSION</span>
                <span className="itemDetailsValue">
                  {selectedFile.fileActiveVersion}
                  {selectedFile.fileActiveVersion <
                  Object.keys(selectedFile.fileVersions).length
                    ? "*"
                    : ""}
                </span>
                <span className="itemDetailsLabel">UPLOADED</span>
                <span className="itemDetailsValue">
                  {FormatDateFE(
                    selectedFile.fileVersions[selectedFile.fileActiveVersion]
                      .fileUploadDate,
                    true
                  )}
                </span>
                <span className="itemDetailsLabel">BY</span>
                <span className="itemDetailsValue">
                  {
                    selectedFile.fileVersions[selectedFile.fileActiveVersion]
                      .fileUploadedBy
                  }
                </span>
              </div>
              <div className="itemDetailsActions">
                {selectedFile.fileType === "ifc" ? (
                  <MenuButton
                    imageSource="assets/icons/view.png"
                    alternativeText="Open in 3D viewer"
                    buttonSize="small"
                    buttonAccent="primary"
                    dispatch={() => {
                      setViewerOptionsDialogReveal(true);
                    }}
                  />
                ) : null}
                {selectedFile.fileType === "pdf" ? (
                  // No choice of viewer to make, so it opens straight away.
                  // Any project member may view, the USER role included,
                  // although that role cannot download.
                  <MenuButton
                    imageSource="assets/icons/view.png"
                    alternativeText="Open in PDF viewer"
                    buttonSize="small"
                    buttonAccent="primary"
                    dispatch={() => {
                      dispatch({
                        type: "RETRIEVE_PDF",
                        payload: {
                          ...selectedFile,
                          type: "PdfRetrieveInterface",
                        } as PdfRetrieveInterface,
                      });
                      navigate("/PDF");
                    }}
                  />
                ) : null}
                <MenuButton
                  imageSource="assets/icons/versions.png"
                  alternativeText="Versions"
                  buttonSize="small"
                  dispatch={() => {
                    setFileVersionDialogReveal(true);
                  }}
                />
                <MenuButton
                  imageSource="assets/icons/download.png"
                  alternativeText="Download"
                  buttonSize="small"
                  dispatch={() => {
                    setDownloadFile(true);
                  }}
                />
                <MenuButton
                  imageSource="assets/icons/delete.png"
                  alternativeText="Delete"
                  buttonSize="small"
                  buttonAccent="destructive"
                  dispatch={() => {
                    setDeleteFileDialogReveal(true);
                  }}
                />
              </div>
            </div>
          );
        } else {
          return <></>;
        }
      })()}
    </>
  );
};

export default FileInfo;
